import { useCallback } from 'react';
import type { TypedDocumentNode } from '@apollo/client';
import { useMutation, useApolloClient } from '@apollo/client/react';
import RefreshTokenService from 'services/RefreshTokenService';
import requireValue from 'utils/requireValue';
import { formatGraphqlErrors } from 'utils/helpers';
import {
  MeDocument,
  type LoginWithPasswordMutation,
  type LoginWithPasswordMutationVariables,
  type MeQuery,
} from 'graphql/generated/graphql';

/** The payload of the auth mutation (`loginWithPassword`): the refresh token and the user. */
export type TAuthResult = NonNullable<LoginWithPasswordMutation['loginWithPassword']>;

export type TAuth = (variables: LoginWithPasswordMutationVariables) => Promise<TAuthResult>;

const useAuth = (
  mutation: TypedDocumentNode<LoginWithPasswordMutation, LoginWithPasswordMutationVariables>,
): [TAuth, boolean] => {
  const [authMutation, { loading }] = useMutation(mutation);
  const client = useApolloClient();
  const auth = useCallback(
    async (variables: LoginWithPasswordMutationVariables) => {
      try {
        const res = await authMutation({ variables });
        // Object.values threw for a result without data, as it did.
        if (res.data === undefined || res.data === null)
          throw new TypeError('Cannot convert undefined or null to object');
        // The payload of the mutation's only root field (formerly `first(Object.values(data))`).
        const resultData = requireValue(res.data.loginWithPassword, 'refreshToken');

        if (resultData.refreshToken) {
          RefreshTokenService.setRefreshToken(resultData.refreshToken);
        }

        client.writeQuery<MeQuery>({
          query: MeDocument,
          data: { me: resultData.user },
        });
        return resultData;
      } catch (error) {
        throw formatGraphqlErrors(error);
      }
    },
    [authMutation, client],
  );

  return [auth, loading];
};

export default useAuth;
